import { ReactElement, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import useLatest from 'react-use/lib/useLatest'
import {
  getShapeLength,
  snapRelativeInputValue,
  getTopDomains,
  getDomainAndRelativePositionFromPoint,
  useModelClickListenerV2,
} from '@scene'
import { find, every, reject, some, uniq, without } from 'lodash-es'
import { Vector3 } from 'three'
import Delete from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AccordionSummary, AccordionDetails, Typography, IconButton } from '@mui/material'
import { ThreeEvent } from '@react-three/fiber'
import { AddButton } from '@ui/actions'
import { FormAccordion } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { useConfigStore } from '@stores'
import { useControlStore, useModelStore, useTransmissionStore } from '@editorStores'
import {
  useElementLabel,
  useElementTypes,
  useHighlightSecondary,
  useModelClickListeners,
  useSelectionMode,
  useStiffeningSnapValues,
} from '@editorHooks'
import { TensileTargetFormItem, TensileTransmitterFormValues } from '../types'
import {
  applyTensileFormValues,
  createSupportTarget,
  createTensileSupport,
  hasLinkedTargetOnElement,
  hasSupportWithTargetsOnElement,
  TensileSupportWithTargets,
} from '../utils/graph'
import FormFields from './FormFields'

interface Props {
  support: TensileSupportWithTargets
  /** Position of each target in the form's `targets` */
  targetIndexByGuid: Map<string, number>
  /** The target whose accordion is open (the one "Neu zuweisen" reassigns) */
  activeTargetGuid?: string
  /** Opens a target; `null` falls back to the first target */
  onActiveTargetChange: (targetGuid: string | null) => void
}

const TensileTransmitterForm = ({
  support,
  targetIndexByGuid,
  activeTargetGuid,
  onActiveTargetChange,
}: Props): ReactElement => {
  const appConfig = useConfigStore.getState()
  const actionMode = useControlStore(state => state.actionMode)
  const { setSelectionMode, unsetSelectionMode, selectionModeKey } = useSelectionMode()
  const model = useModelStore(state => state.model)
  const savedGraph = useTransmissionStore(state => state.tensileTransmissionGraph)
  const { getValues, setValue } = useFormContext<TensileTransmitterFormValues>()

  const getElementLabel = useElementLabel()

  const [getSnapValues] = useStiffeningSnapValues()
  const { enqueueSnackbar } = useSnackbar()
  const elementTypes = useElementTypes()

  const { targets } = support

  /** The project graph including the form's unsaved edits, for lookups across elements */
  const getEditedGraph = () => applyTensileFormValues(savedGraph, support.element_guid, getValues())

  const setTargets = (update: (targets: TensileTargetFormItem[]) => TensileTargetFormItem[]) =>
    setValue('targets', update(getValues('targets')), { shouldDirty: true })

  const addTarget = (target: ElementSupportItem) =>
    setTargets(targets => [...targets, { ...target, support_guid: support.guid }])

  const hasSavedTensileSupport = (elementGuid: string) =>
    some(savedGraph.element_supports, ['element_guid', elementGuid])

  /** Gives a column or rip that became a target a tensile support, unless it has one */
  const ensureTensileSupport = (elementGuid: string, domainGuid: string) => {
    const removedSupportElementGuids = getValues('removedSupportElementGuids')

    // Only saved supports are listed as removed: dropping that removal brings them back
    if (removedSupportElementGuids.includes(elementGuid)) {
      setValue('removedSupportElementGuids', without(removedSupportElementGuids, elementGuid), {
        shouldDirty: true,
      })
      return
    }

    if (getEditedGraph().element_supports.some(({ element_guid }) => element_guid === elementGuid))
      return

    setValue(
      'addedSupports',
      [...getValues('addedSupports'), createTensileSupport(elementGuid, domainGuid, 0)],
      { shouldDirty: true },
    )
  }

  /** Drops the tensile supports of a column or rip (effectively removing its stiffening) */
  const removeTensileSupports = (elementGuid: string) => {
    setValue('addedSupports', reject(getValues('addedSupports'), ['element_guid', elementGuid]), {
      shouldDirty: true,
    })

    // A support added in this form is simply gone again, only saved ones need removing
    if (!hasSavedTensileSupport(elementGuid)) return

    setValue(
      'removedSupportElementGuids',
      uniq([...getValues('removedSupportElementGuids'), elementGuid]),
      { shouldDirty: true },
    )
  }

  const isFoundationSupport = useMemo(() => {
    return !!targets.length && every(targets, ['target_type', 'foundation'])
  }, [targets])

  // `targets` is a new array on every edit, the key keeps the highlighted ids stable meanwhile
  const targetElementGuidsKey = uniq(targets.map(({ element_guid }) => element_guid)).join(',')
  const targetElementGuids = useMemo(
    () => (targetElementGuidsKey ? targetElementGuidsKey.split(',') : []),
    [targetElementGuidsKey],
  )

  // All targets of the open transmitter are highlighted in the scene
  useHighlightSecondary(targetElementGuids)

  /** Length of the wall domain the target sits on, to show its position in m */
  const getTargetDomainLength = ({ element_guid, domain_guid }: ElementSupportItem) => {
    const domains = find(model.walls, ['guid', element_guid])?.domains || []
    const domain = find(domains, ['guid', domain_guid]) as Domain

    if (!domain) return 0

    return Number(getShapeLength([domain.start, domain.end]).toFixed(2))
  }

  const updateTargetRef = useLatest((elementGuid: string) => {
    if (elementGuid === support.element_guid) {
      unsetSelectionMode()
      return enqueueSnackbar('Ziel kann nicht auf dem selben Element liegen', {
        variant: 'error',
      })
    }

    if (!activeTargetGuid) return

    const newDomainGuid = find(model.walls, ['guid', elementGuid])?.domains?.[2].guid

    if (!newDomainGuid) return

    setTargets(targets =>
      targets.map(target =>
        target.guid === activeTargetGuid
          ? // The embedded domain belongs to the old wall, `domain_guid` identifies the new one
            { ...target, domain: null, element_guid: elementGuid, domain_guid: newDomainGuid }
          : target,
      ),
    )
    unsetSelectionMode()
  })

  const updateTarget = useCallback(
    (elementGuid: string) => {
      updateTargetRef.current(elementGuid)
    },
    [updateTargetRef],
  )

  // update target element
  useModelClickListeners(
    (event: ThreeEvent<MouseEvent>) => {
      const elementGuid = event.object.name

      updateTarget(elementGuid)
    },
    [activeTargetGuid, unsetSelectionMode],
    selectionModeKey === 'update-target-element',
    true,
  )

  useModelClickListenerV2('position', updateTarget, {
    enabled: selectionModeKey === 'update-target-element' && actionMode !== 'hide',
    guids: null,
  })

  const handleDeleteTarget = (event: React.MouseEvent, target: ElementSupportItem) => {
    event.stopPropagation()

    const targetElementType = elementTypes[target.element_guid]
    if (
      targetElementType === undefined &&
      !model.foundationElements.some(foundation => foundation.guid === target.element_guid)
    ) {
      throw Error('Encountered strange undefined element type')
    }

    // Once no transmitter targets the column / rip anymore, its support goes too (effectively
    // removing the stiffening from the column / rip)
    const editedGraph = getEditedGraph()
    const removesSupports =
      (targetElementType === 'columns' || targetElementType === 'rips') &&
      !hasLinkedTargetOnElement(
        {
          ...editedGraph,
          support_targets: reject(editedGraph.support_targets, ['target_guid', target.guid]),
        },
        target.element_guid,
      )

    // That would silently drop the targets of the column / rip as well, so they go first
    if (removesSupports && hasSupportWithTargetsOnElement(editedGraph, target.element_guid)) {
      enqueueSnackbar(
        `${getElementLabel(
          target.element_guid,
        )} leitet die Zugkraft noch weiter. Bitte entfernen Sie zuerst dessen Ziele`,
        { variant: 'error' },
      )
      return
    }

    enqueueSnackbar('Verbindung entfernt. Änderungen müssen noch gespeichert werden', {
      variant: 'info',
    })

    setTargets(targets => reject(targets, ['guid', target.guid]))

    if (removesSupports) removeTensileSupports(target.element_guid)

    onActiveTargetChange(null)
  }

  const addNewTargetRef = useLatest((elementGuid: string, point: Vector3) => {
    if (elementGuid === support.element_guid) {
      unsetSelectionMode()
      return enqueueSnackbar('Ziel kann nicht auf dem selben Element liegen', {
        variant: 'error',
      })
    }

    const targetElementType = elementTypes[elementGuid]
    if (targetElementType === undefined) throw Error('Encountered strange undefined element type')

    // TODO: Remove this when non-wall tensile is enabled
    if (
      !appConfig.ff_non_wall_tensile &&
      !(targetElementType === 'inner_walls' || targetElementType === 'outer_walls')
    ) {
      return
    }

    if (targetElementType === 'inner_walls' || targetElementType === 'outer_walls') {
      const wallElement = find(model.walls, ['guid', elementGuid])

      if (!wallElement) throw Error('Cant find wall element on model')
      const elementDomains = wallElement.domains as Domain[]
      const topDomains = getTopDomains(elementDomains)

      if (!topDomains.length) throw Error('Cant find top domain of clicked wall')
      const result = getDomainAndRelativePositionFromPoint(topDomains, point)

      if (!result) return

      const [domain, relative_position] = result
      const snapValues = getSnapValues(elementGuid)
      const snappedPosition = snapRelativeInputValue(relative_position, snapValues, domain.length)

      const newTarget = createSupportTarget(elementGuid, domain.guid, snappedPosition)
      addTarget(newTarget)

      onActiveTargetChange(newTarget.guid)
      unsetSelectionMode()
    } else if (targetElementType === 'rips') {
      const ripElement = find(model.rips, ['position_guid', elementGuid])
      if (!ripElement) throw Error('Cant find rip element on model')
      const newTarget = createSupportTarget(elementGuid, ripElement.domain_guid, 1)
      ensureTensileSupport(elementGuid, ripElement.domain_guid)
      addTarget(newTarget)
      onActiveTargetChange(newTarget.guid)
      unsetSelectionMode()
    } else if (targetElementType === 'columns') {
      const columnElement = find(model.columns, ['guid', elementGuid])
      if (!columnElement) throw Error('Cant find column element on model')
      if (!columnElement.domains) throw Error('Cant find domains on column element')
      if (columnElement.domains.length !== 1)
        throw Error('Column element has number of domains other than 1')

      const domain = columnElement.domains[0]

      const newTarget = createSupportTarget(elementGuid, domain.guid, 1)
      ensureTensileSupport(elementGuid, domain.guid)
      addTarget(newTarget)
      onActiveTargetChange(newTarget.guid)
      unsetSelectionMode()
    }
  })

  const addNewTarget = useCallback(
    (elementGuid: string, event: ThreeEvent<MouseEvent>) => {
      addNewTargetRef.current(elementGuid, event.point)
    },
    [addNewTargetRef],
  )

  useModelClickListeners(
    event => {
      const { name: elementGuid } = event.object
      addNewTarget(elementGuid, event)
    },
    [model, support.guid],
    selectionModeKey === 'add-new-target',
    true,
  )

  useModelClickListenerV2('position', addNewTarget, {
    enabled: selectionModeKey === 'add-new-target' && actionMode !== 'hide',
    guids: null,
  })

  if (isFoundationSupport) {
    return (
      <Box p={2}>
        <Typography variant="body2">Ziel liegt im Fundament</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {targets.map((target, index) => {
        const { guid, element_guid } = target
        const active = activeTargetGuid === guid
        const targetIndex = targetIndexByGuid.get(guid)
        const fieldName = `targets.${targetIndex}`

        return (
          <FormAccordion
            fields={[`${fieldName}.transmission_factor`, `${fieldName}.relative_position`]}
            key={guid}
            expanded={active}
            onChange={(_, expanded) => {
              if (targets.length === 1) return
              if (expanded) onActiveTargetChange(guid)
            }}
            data-cy={`tensile-target-form-${index}`}
          >
            <AccordionSummary
              key={guid}
              expandIcon={targets.length > 1 ? <ExpandMoreIcon /> : null}
              aria-controls="panel1a-content"
              id={guid}
              sx={{
                cursor: targets.length === 1 ? 'default !important' : 'pointer',
                background: ({ palette }) => palette.grey['100'],
              }}
            >
              <Box display="flex" justifyContent="space-between" flexGrow="1">
                <Typography data-cy="tensile-target-title" variant="body2">
                  Ziel {index + 1} - {getElementLabel(target.element_guid)}
                </Typography>
                <IconButton
                  sx={{ padding: 0.5, marginY: -0.5 }}
                  onClick={event => handleDeleteTarget(event, target)}
                >
                  <Delete fontSize="small" data-cy="btn-delete-target" />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                background: ({ palette }) => palette.grey['100'],
              }}
            >
              <FormFields
                // Remount instead of renaming the fields when the target's position in
                // `targets` shifts (e.g. after a delete), so RHF never unregisters the
                // old path that now belongs to another target
                key={fieldName}
                elementGuid={element_guid}
                domainLength={getTargetDomainLength(target)}
                fieldName={fieldName}
                onChangeTargetWall={() =>
                  setSelectionMode({
                    message: 'Klicken Sie ein Wandelement zur Auswahl',
                    key: 'update-target-element',
                  })
                }
                targetElementGuid={target.element_guid}
              />
            </AccordionDetails>
          </FormAccordion>
        )
      })}
      <Box display="flex" justifyContent="end" paddingY={1}>
        <AddButton
          size="small"
          data-cy="btn-add-target"
          onClick={() =>
            setSelectionMode({
              message: 'Klicken Sie ein Wandelement zur Auswahl',
              key: 'add-new-target',
            })
          }
        >
          Ziel Hinzufügen
        </AddButton>
      </Box>
    </Box>
  )
}

export default TensileTransmitterForm
